import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useMenuPermission } from '@src/recoil/permission';
import { PRODUCT_KEYS } from '@src/types/productType';
import Spinner from '@components/molecules/Spinner';

import {
  CallGetOptGroup,
  CallPutOptDisplay,
  CallPutOptGroupStatus,
  CallPutOptStatus,
} from '@src/library/request/callProduct';

const OptionStatus = () => {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const storeId = searchParams.get('storeID');
  const optionGroupID = searchParams.get('optionGroupID');
  const optionID = searchParams.get('optionID');

  const { menuPermission } = useMenuPermission(storeId);

  const [loading, setLoading] = useState(false);
  const [inputData, setInputData] = useState({
    status: true,
    display: true,
  });

  const { data: optDetail, refetch } = useQuery([PRODUCT_KEYS.OPTIONGROUPDETAIL, storeId, optionGroupID, optionID], {
    enabled: true,
    cacheTime: 0,
    queryFn: async () => {
      try {
        setLoading(true);
        const res = await CallGetOptGroup(storeId, optionGroupID);
        if (res.data) {
          const opt = res.data as any;
          if (optionID) {
            const item = opt.children.find((v: any) => v.optionID === Number(optionID));
            setInputData({
              status: item.status === 1,
              display: Boolean(item.isDisplay),
            });
          } else {
            setInputData({
              status: opt.status === 1,
              display: Boolean(opt.isDisplay),
            });
          }
        }

        return res.data as any;
      } catch (error) {
        console.log('error', error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
  });

  // isNoAuth11 로직과 유사: 노출/품절 권한 개별 체크
  // permission.ts의 isNoAuth11과 동일한 방식으로 permissionType === 2 (수정권한)일 때
  // display(노출) 권한과 soldOut(품절) 권한을 개별적으로 확인하여 비활성화 상태를 결정
  const disableState = useMemo(() => {
    const state = { status: false, display: false };
    if (!menuPermission.isFetch || !menuPermission.data?.[0]) {
      return state;
    }
    const permission = menuPermission.data[0];
    const {
      permissionType, // 권한없음:0, 전체권한:1, 수정권한:2
      display, // 노출
      soldOut, // 품절
    } = permission;

    // 수정권한 (노출수정 or 품절수정)
    if (permissionType === 2) {
      if (display === 1 && soldOut === 1) return state;
      if (display === 1) {
        state.status = true; // 노출수정 경우 판매중 비활성화 처리
      }
      if (soldOut === 1) {
        // 품절수정인 경우 노출 비활성화
        state.display = true;
      }
    }

    return state;
  }, [menuPermission, optDetail]);

  const saveOptionDisplay = async () => {
    const msg = inputData.display ? '비노출' : '노출';
    if (!window.confirm(`${msg}로 설정하시겠습니까?`)) {
      return;
    }

    setLoading(true);

    setInputData((prev) => {
      return { display: !prev.display, status: prev.status };
    });

    if (optionID) {
      try {
        const res = await CallPutOptDisplay(storeId, optionID, { isDisplay: Number(!inputData.display) });
        if (res.result.code === 1) {
          window.alert(`${msg}로 설정되었습니다.`);
        } else {
          window.alert(res.result.message);
        }
      } catch (error) {
        console.log('error', error);
        throw error;
      } finally {
        queryClient.invalidateQueries([PRODUCT_KEYS.OPTIONGROUP, storeId]);
        setLoading(false);
        refetch();
      }
    } else {
      try {
        const res = await CallPutOptGroupStatus(storeId, optionGroupID, { isDisplay: Number(!inputData.display) });
        if (res.result.code === 1) {
          window.alert(`${msg}로 설정되었습니다.`);
        } else {
          window.alert(res.result.message);
        }
      } catch (error) {
        console.log('error', error);
        throw error;
      } finally {
        queryClient.invalidateQueries([PRODUCT_KEYS.OPTIONGROUP, storeId]);
        setLoading(false);
        window.history.back();
      }
    }
  };

  const saveOptionStatus = async () => {
    const status = !inputData.status ? 'sale' : 'soldout';
    const msg = {
      sale: ['품절 설정을 해제', '품절 해제'],
      soldout: ['품절로 설정', '품절로 설정'],
    };

    if (!window.confirm(`${msg[status][0]}하시겠습니까?`)) {
      return;
    }

    setLoading(true);

    setInputData((prev) => {
      return { display: prev.display, status: !prev.status };
    });

    try {
      const res = await CallPutOptStatus(storeId, optionID, status);
      if (res.result.code === 1) {
        window.alert(`${msg[status][1]}되었습니다.`);
      }
    } catch (error) {
      console.log('error', error);
      throw error;
    } finally {
      queryClient.invalidateQueries([PRODUCT_KEYS.OPTIONGROUP, storeId]);
      setLoading(false);
      refetch();
    }
  };

  const titleName = useMemo(() => {
    if (!optDetail) return '';

    if (optionID) {
      return optDetail.children.find((v: any) => v.optionID === Number(optionID))?.name;
    }
    return optDetail.title;
  }, [optDetail]);

  const hasCoupon = useMemo(() => {
    if (!optDetail) return false;

    const hasMenuCoupon = optDetail.children.some((option: any) => option.menuCouponCount > 0);
    if (hasMenuCoupon) {
      return true;
    }

    return false;
  }, [optDetail]);

  useEffect(() => {}, []);

  return (
    <article id="mArticle" className="art-product">
      {/* <h3 className="screen-out">옵션 노출 품절 설정 본문</h3> */}
      {/* <div className="tip-product">
        <em className="emph-tip-product">TIP</em>
        <strong>위메프오 앱</strong>에서는 이렇게 노출됩니다.
        <a href="#none" className="link-tip-product">
          예시보기
        </a>
      </div> */}
      <div className="view-product option-status">
        <div className="box-product ">
          {optionID && hasCoupon && <span className="badge-coupon">메뉴 쿠폰</span>}
          <strong id="title" className="lab-product-status">
            {titleName}
          </strong>
          <div className="group-switch-check">
            <label htmlFor="isDisplay" className="switch-check-s">
              <input
                type="checkbox"
                id="isDisplay"
                checked={inputData.display}
                disabled={disableState.display}
                onChange={saveOptionDisplay}
              />
              <span className="txt-check unchecked">비노출</span>
              <span className="txt-check checked">노출</span>
              <span className="ico-check" />
            </label>
            {optionID && (
              <label htmlFor="status" className="switch-check-s">
                <input
                  type="checkbox"
                  id="status"
                  checked={inputData.status}
                  disabled={disableState.status}
                  onChange={saveOptionStatus}
                />
                <span className="txt-check unchecked">품절</span>
                <span className="txt-check checked">판매중</span>
                <span className="ico-check" />
              </label>
            )}
          </div>
        </div>
        {!optionID && (
          <div id="optionBox" className="box-option-status">
            <ul id="optionList" className="list-option">
              {optDetail &&
                optDetail.children.map((v: any, idx: number) => {
                  return (
                    <li key={idx}>
                      {v.menuCouponCount > 0 && <span className="badge-coupon">메뉴 쿠폰</span>}
                      {v.name}
                    </li>
                  );
                })}
            </ul>
          </div>
        )}
        <div className="box-option-status">
          <ul className="txt-muted">
            <li>비노출로 설정한 메뉴 및 옵션은 고객 주문화면에 게시되지 않습니다.</li>
            {optionID && (
              <li>
                품절 설정 시 설정한 당일 영업시간 마감까지 유지되며, 다음날 자동 해제 됩니다. (24시간 운영 매장은 오전
                5시에 자동으로 품절 해제됩니다.)
              </li>
            )}
          </ul>
        </div>
      </div>
      <Spinner show={loading} />
    </article>
  );
};

export default OptionStatus;
